// src/views/results.ts
import type { Puzzle, RoundResult, ChallengeContext } from '../types';
import { shareText } from '../core/share';
import { dayNameFor } from '../core/dayName';
import { hslToHex } from '../core/color';
import { challengeUrl, challengeComparisonLine } from '../core/challenge';

type ResultsOpts = {
  puzzle: Puzzle;
  results: RoundResult[];
  total: number;
  streak: number;
  onShare: (text: string) => void;
  onViewStats: () => void;
  archiveIso?: string;
  challenge?: ChallengeContext | null;
  onChallenge?: (url: string) => void;
  onViewArchive?: () => void;
  onBackToArchive?: () => void;
  onRate?: (rating: number) => void;
  onFeedback?: (text: string) => void;
  alreadyRated?: boolean;
};

// Module-scoped countdown interval.
let countdownInterval: ReturnType<typeof setInterval> | null = null;

export function disposeResults(): void {
  if (countdownInterval !== null) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function msUntilNextLocalMidnight(): number {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  return Math.max(0, next.getTime() - now.getTime());
}

function formatHMS(totalMs: number): string {
  const totalSec = Math.floor(totalMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function renderResults(root: HTMLElement, opts: ResultsOpts): void {
  disposeResults();

  const rows = opts.results.map((r, i) => {
    const targetHex = hslToHex(r.target);
    const guessHex = hslToHex(r.guess);
    const ce = r.channelErrors;
    const dh = Math.round(ce.hue);
    const ds = Math.round(ce.sat);
    const dl = Math.round(ce.light);
    return `
    <li class="results-row-group reveal reveal-row-${i}">
      <button class="results-row" type="button"
              aria-expanded="false"
              aria-controls="row-detail-${i}"
              data-row-index="${i}">
        <span class="results-row-num">${i + 1}</span>
        <span class="results-row-color" aria-label="Target ${targetHex}">
          <span class="results-row-swatch" style="background: hsl(${r.target.h}, ${r.target.s}%, ${r.target.l}%);"></span>
          <span class="results-row-hex">${targetHex}</span>
        </span>
        <span class="results-row-color" aria-label="Your guess ${guessHex}">
          <span class="results-row-swatch" style="background: hsl(${r.guess.h}, ${r.guess.s}%, ${r.guess.l}%);"></span>
          <span class="results-row-hex">${guessHex}</span>
        </span>
        <span class="results-row-score">
          <span class="results-row-score-top">${r.score}<span class="results-row-chevron" aria-hidden="true">\u203a</span></span>
          <span class="results-row-score-detail" id="row-detail-${i}" aria-hidden="true">H \u00b1${dh}\u00b0 &middot; S \u00b1${ds} &middot; L \u00b1${dl}</span>
        </span>
      </button>
    </li>
  `;
  }).join('');

  const isArchive = !!opts.archiveIso;
  const ariaLabel = isArchive
    ? `Barve \u2116${opts.puzzle.dayNumber} archive result, ${opts.total} out of 100`
    : `Share your Barve \u2116${opts.puzzle.dayNumber} result, ${opts.total} out of 100`;

  const heroHtml = isArchive
    ? `<div class="archive-result-hero reveal reveal-share">
        <span class="share-hero-meta">Barve \u2116${opts.puzzle.dayNumber} <span class="archive-badge">archive</span></span>
        <span class="share-hero-score">${opts.total}<span class="share-hero-of">/100</span></span>
      </div>`
    : `<button class="share-hero reveal reveal-share" id="share" type="button" aria-label="${escapeHtml(ariaLabel)}">
        <span class="share-hero-meta">Barve \u2116${opts.puzzle.dayNumber}</span>
        <span class="share-hero-score">${opts.total}<span class="share-hero-of">/100</span></span>
        <span class="share-hero-cta">Share<span class="share-hero-arrow" aria-hidden="true">\u2192</span></span>
      </button>`;

  const challengeHtml = isArchive
    ? ''
    : `<div class="challenge-block reveal reveal-rating">
        ${opts.challenge ? `<p class="challenge-comparison">${escapeHtml(challengeComparisonLine(opts.total, opts.challenge.score))}</p>` : ''}
        <button class="challenge-btn" id="challenge" type="button">Challenge a friend<span class="share-hero-arrow" aria-hidden="true">\u2192</span></button>
      </div>`;

  const footerLinks = isArchive
    ? `<button class="results-stats-link reveal reveal-stats" id="back-archive" type="button">Back to archive</button>`
    : `<button class="results-stats-link reveal reveal-stats" id="stats" type="button">View stats</button>
      ${opts.onViewArchive ? '<button class="results-stats-link reveal reveal-archive" id="archive" type="button">Archive</button>' : ''}
      <p class="next-puzzle-countdown" id="countdown">Next puzzle in <span class="countdown-clock">${formatHMS(msUntilNextLocalMidnight())}</span></p>`;

  const ratingHtml = (!isArchive && opts.onRate)
    ? `<div class="puzzle-rating reveal reveal-rating" id="puzzle-rating">${
        opts.alreadyRated
          ? '<p class="puzzle-rating-noted">noted.</p>'
          : `<p class="puzzle-rating-question">How was today\u2019s puzzle?</p>
             <div class="puzzle-rating-options">
               <button class="puzzle-rating-btn" type="button" data-rating="1">loved it</button>
               <span class="puzzle-rating-sep" aria-hidden="true">\u00b7</span>
               <button class="puzzle-rating-btn" type="button" data-rating="2">good</button>
               <span class="puzzle-rating-sep" aria-hidden="true">\u00b7</span>
               <button class="puzzle-rating-btn" type="button" data-rating="3">meh</button>
             </div>`
      }</div>`
    : '';

  root.innerHTML = `
    <header class="app-header">
      <span class="brand">Barve \u2116${opts.puzzle.dayNumber}</span>
      <span>Results</span>
    </header>
    <div class="card">
      ${heroHtml}
      ${challengeHtml}
      ${ratingHtml}
      <div class="results-breakdown-block" aria-label="Round by round breakdown">
        <div class="results-breakdown-headers reveal reveal-headers" aria-hidden="true">
          <span></span>
          <span>Target</span>
          <span>Your guess</span>
          <span>Score</span>
        </div>
        <ol class="results-breakdown">
          ${rows}
        </ol>
      </div>
      ${footerLinks}
    </div>
  `;

  if (!isArchive) {
    root.querySelector('#share')!.addEventListener('click', () => {
      opts.onShare(shareText(opts.puzzle, opts.results, opts.total, {
        streak: opts.streak,
        dayName: dayNameFor(opts.puzzle),
      }));
    });
    root.querySelector('#stats')!.addEventListener('click', opts.onViewStats);
    const archiveBtn = root.querySelector('#archive');
    if (archiveBtn && opts.onViewArchive) {
      archiveBtn.addEventListener('click', opts.onViewArchive);
    }
    const challengeBtn = root.querySelector('#challenge');
    if (challengeBtn && opts.onChallenge) {
      challengeBtn.addEventListener('click', () => {
        opts.onChallenge!(challengeUrl(opts.puzzle.dayNumber, opts.total));
      });
    }
  } else if (opts.onBackToArchive) {
    root.querySelector('#back-archive')!.addEventListener('click', opts.onBackToArchive);
  }

  if (!isArchive && opts.onRate && !opts.alreadyRated) {
    const ratingEl = root.querySelector('#puzzle-rating');
    if (ratingEl) {
      let ratingInFlight = false;
      ratingEl.querySelectorAll<HTMLButtonElement>('.puzzle-rating-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (ratingInFlight) return;
          ratingInFlight = true;
          const rating = Number(btn.dataset.rating);

          // Highlight chosen, fade others
          ratingEl.querySelectorAll<HTMLButtonElement>('.puzzle-rating-btn').forEach((b) => {
            if (b === btn) {
              b.classList.add('puzzle-rating-chosen');
            } else {
              b.classList.add('puzzle-rating-faded');
            }
          });
          ratingEl.querySelectorAll('.puzzle-rating-sep').forEach((sep) => {
            (sep as HTMLElement).classList.add('puzzle-rating-faded');
          });

          const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const delay = prefersReduced ? 0 : 600;

          const showFollowUp = () => {
            ratingEl.innerHTML = `
              <p class="puzzle-rating-question">anything to add?</p>
              <div class="puzzle-rating-followup">
                <input class="puzzle-rating-input" type="text" maxlength="140"
                       placeholder="optional" autocomplete="off" />
                <button class="puzzle-rating-send" type="button">send</button>
              </div>`;
            const input = ratingEl.querySelector('.puzzle-rating-input') as HTMLInputElement;
            const sendBtn = ratingEl.querySelector('.puzzle-rating-send') as HTMLButtonElement;
            let sent = false;

            const finish = (text?: string) => {
              if (sent) return;
              sent = true;
              if (text && opts.onFeedback) opts.onFeedback(text);
              ratingEl.innerHTML = '<p class="puzzle-rating-noted">noted.</p>';
            };

            sendBtn.addEventListener('click', () => {
              finish(input.value.trim() || undefined);
            });
            input.addEventListener('keydown', (e: KeyboardEvent) => {
              if (e.key === 'Enter') finish(input.value.trim() || undefined);
            });

            // Auto-dismiss after 15s if they don't interact
            const autoTimer = setTimeout(() => finish(), 15000);
            input.addEventListener('focus', () => clearTimeout(autoTimer));

            input.focus();
          };

          if (delay === 0) {
            showFollowUp();
          } else {
            setTimeout(showFollowUp, delay);
          }

          opts.onRate!(rating);
        });
      });
    }
  }

  const rowButtons = root.querySelectorAll<HTMLButtonElement>('button.results-row');
  rowButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const wasOpen = btn.getAttribute('aria-expanded') === 'true';
      rowButtons.forEach((other) => {
        if (other !== btn) other.setAttribute('aria-expanded', 'false');
      });
      btn.setAttribute('aria-expanded', String(!wasOpen));
    });
  });

  if (!isArchive) {
    const tick = () => {
      const clockEl = document.querySelector('#countdown .countdown-clock');
      if (clockEl) clockEl.textContent = formatHMS(msUntilNextLocalMidnight());
    };
    countdownInterval = setInterval(tick, 1000);
  }
}

export function showToast(message: string, ms = 2000): void {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = message;
  document.body.appendChild(el);
  void el.offsetHeight;
  el.classList.add('visible');
  setTimeout(() => {
    el.classList.remove('visible');
    setTimeout(() => el.remove(), 300);
  }, ms);
}
